import apiClient, { ApiRequestError, getApiErrorMessage, handleUnauthorized, readFetchApiError } from './apiClient';
import { getAuthToken } from '../utils/storage';
import { DEFAULT_LLM_MODEL_KEY } from '../constants/llmModels';
import { DEFAULT_LLM_PROVIDER_KEY } from '../constants/llmProviders';

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped?.items)) return unwrapped.items;
  if (Array.isArray(unwrapped?.Items)) return unwrapped.Items;
  return [];
};

export const normalizeConversation = (item) => ({
  id: item?.Id || item?.id,
  title: item?.Title || item?.title || '',
  model: item?.Model || item?.model || DEFAULT_LLM_MODEL_KEY,
  provider: item?.Provider || item?.provider || DEFAULT_LLM_PROVIDER_KEY,
  createdAt: item?.CreatedAt || item?.createdAt || new Date().toISOString(),
  updatedAt: item?.UpdatedAt || item?.updatedAt || new Date().toISOString(),
  isPinned: Boolean(item?.IsPinned ?? item?.isPinned),
  isArchived: Boolean(item?.IsArchived ?? item?.isArchived),
  isTemporary: Boolean(item?.IsTemporary ?? item?.isTemporary),
  expiresAt: item?.ExpiresAt || item?.expiresAt || null,
  folderId: item?.FolderId || item?.folderId || null
});

export const normalizeConversationMessage = (message, index = 0) => {
  const text = message?.Message || message?.message || '';
  return {
    id: message?.Id || message?.id || `msg-${index}`,
    conversationId: message?.ConversationId || message?.conversationId,
    role: `${message?.Role || message?.role || 'assistant'}`.toLowerCase(),
    provider: message?.Provider || message?.provider || null,
    model: message?.Model || message?.model || null,
    message: text,
    content: text,
    imageUrls: message?.ImageUrls || message?.imageUrls || [],
    generatedImageUrls: message?.GeneratedImageUrls || message?.generatedImageUrls || [],
    feedback: message?.Feedback || message?.feedback || null,
    quotedText: message?.QuotedText || message?.quotedText || null,
    quotedMessageId: message?.QuotedMessageId || message?.quotedMessageId || null,
    createdAt: message?.CreatedAt || message?.createdAt || new Date().toISOString(),
    updatedAt: message?.UpdatedAt || message?.updatedAt || new Date().toISOString()
  };
};

export const fetchConversationsApi = async ({ archived } = {}) => {
  try {
    const { data } = await apiClient.get('/conversations', {
      params: archived === undefined ? undefined : { archived }
    });
    return unwrapApiList(data).map(normalizeConversation).filter((item) => item.id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load conversations.'));
  }
};

export const fetchConversationApi = async (id) => {
  try {
    const { data } = await apiClient.get(`/conversations/${encodeURIComponent(id)}`);
    return normalizeConversation(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load conversation.'));
  }
};

export const createConversationApi = async ({
  title = '',
  model = '',
  provider = '',
  isTemporary = false,
  folderId = null
} = {}) => {
  try {
    const payload = {};
    if (title) payload.Title = title;
    if (model) payload.Model = model;
    if (provider) payload.Provider = provider;
    if (isTemporary) payload.IsTemporary = true;
    if (folderId) payload.FolderId = folderId;
    const { data } = await apiClient.post('/conversations', payload);
    return normalizeConversation(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create conversation.'));
  }
};

export const updateConversationApi = async (id, { title, model, provider, isPinned, isArchived } = {}) => {
  try {
    const payload = {};
    if (title !== undefined) payload.Title = title;
    if (model !== undefined) payload.Model = model;
    if (provider !== undefined) payload.Provider = provider;
    if (isPinned !== undefined) payload.IsPinned = isPinned;
    if (isArchived !== undefined) payload.IsArchived = isArchived;
    const { data } = await apiClient.put(`/conversations/${encodeURIComponent(id)}`, payload);
    return normalizeConversation(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update conversation.'));
  }
};

export const deleteConversationApi = async (id) => {
  try {
    await apiClient.delete(`/conversations/${encodeURIComponent(id)}`);
    return id;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete conversation.'));
  }
};

export const fetchConversationMessagesApi = async (conversationId) => {
  try {
    const { data } = await apiClient.get(`/conversations/${encodeURIComponent(conversationId)}/messages`);
    return unwrapApiList(data).map(normalizeConversationMessage);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load messages.'));
  }
};

export const setConversationMessageFeedbackApi = async (messageId, feedback) => {
  try {
    const { data } = await apiClient.put(`/conversations/messages/${encodeURIComponent(messageId)}/feedback`, {
      Feedback: feedback
    });
    return normalizeConversationMessage(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to save feedback.'));
  }
};

const buildSseEvent = (rawEvent) => {
  const lines = rawEvent.split(/\r?\n/);
  let eventName = '';
  const dataLines = [];
  lines.forEach((line) => {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });
  return { eventName, data: dataLines.join('\n') };
};

const consumeConversationSse = async (response, { onStart, onDelta, onImage, onResult, onTitle, onDone, signal }) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  const abortRead = () => {
    reader.cancel().catch(() => {});
  };
  signal?.addEventListener?.('abort', abortRead, { once: true });
  if (signal?.aborted) {
    abortRead();
    throw new DOMException('Aborted', 'AbortError');
  }

  try {
    while (true) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      let chunk;
      try {
        chunk = await reader.read();
      } catch (error) {
        if (signal?.aborted || error?.name === 'AbortError') {
          throw new DOMException('Aborted', 'AbortError');
        }
        throw error;
      }

      const { done, value } = chunk;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        if (!part.trim()) continue;
        const { eventName, data } = buildSseEvent(part);
        if (eventName === 'start') onStart?.();
        else if (eventName === 'delta') {
          try {
            const payload = JSON.parse(data);
            const content = payload?.content || '';
            fullText += content;
            onDelta?.(content, fullText);
          } catch {
            fullText += data;
            onDelta?.(data, fullText);
          }
        } else if (eventName === 'image') {
          try {
            const payload = JSON.parse(data);
            onImage?.(payload?.url || '');
          } catch {
            onImage?.(data);
          }
        } else if (eventName === 'title') {
          try {
            const payload = JSON.parse(data);
            onTitle?.(payload?.title || payload?.content || '');
          } catch {
            onTitle?.(data);
          }
        } else if (eventName === 'result') {
          try {
            onResult?.(JSON.parse(data), fullText);
          } catch {
            onResult?.(null, fullText);
          }
        } else if (eventName === 'done') {
          onDone?.(fullText);
        }
      }
    }

    return fullText;
  } finally {
    signal?.removeEventListener?.('abort', abortRead);
  }
};

const postConversationSse = async (path, body, handlers, signal) => {
  const token = getAuthToken();
  const response = await fetch(`${apiClient.defaults.baseURL}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body),
    signal
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    throw new ApiRequestError(await readFetchApiError(response), response.status);
  }

  return consumeConversationSse(response, { ...handlers, signal });
};

export const streamConversationMessageApi = async ({
  conversationId,
  prompt,
  provider = DEFAULT_LLM_PROVIDER_KEY,
  model = DEFAULT_LLM_MODEL_KEY,
  generateImage = false,
  enableWebSearch = false,
  mode = null,
  imageUrls = [],
  quotedText = null,
  quotedMessageId = null,
  signal,
  onStart,
  onDelta,
  onImage,
  onResult,
  onTitle,
  onDone
}) =>
  postConversationSse(
    '/conversations/generate',
    {
      ConversationId: conversationId,
      Prompt: prompt,
      Provider: provider,
      Model: model,
      GenerateImage: Boolean(generateImage),
      EnableWebSearch: Boolean(enableWebSearch),
      Mode: mode || null,
      ImageUrls: Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [],
      QuotedText: quotedText || null,
      QuotedMessageId: quotedMessageId || null
    },
    { onStart, onDelta, onImage, onResult, onTitle, onDone },
    signal
  );

export const regenerateConversationMessageApi = async ({
  messageId,
  provider = DEFAULT_LLM_PROVIDER_KEY,
  model = DEFAULT_LLM_MODEL_KEY,
  signal,
  onStart,
  onDelta,
  onImage,
  onResult,
  onTitle,
  onDone
}) =>
  postConversationSse(
    `/conversations/messages/${encodeURIComponent(messageId)}/regenerate`,
    { Provider: provider, Model: model },
    { onStart, onDelta, onImage, onResult, onTitle, onDone },
    signal
  );

export const editConversationMessageApi = async ({
  messageId,
  message,
  provider = DEFAULT_LLM_PROVIDER_KEY,
  model = DEFAULT_LLM_MODEL_KEY,
  signal,
  onStart,
  onDelta,
  onImage,
  onResult,
  onTitle,
  onDone
}) =>
  postConversationSse(
    `/conversations/messages/${encodeURIComponent(messageId)}/edit`,
    { Message: message, Provider: provider, Model: model },
    { onStart, onDelta, onImage, onResult, onTitle, onDone },
    signal
  );
